import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { useEntity } from '../../contexts/EntityContext';
import { useAuth } from '../../contexts/AuthContext';
import { api, ApiError, Wallet } from '../../lib/api';
import Modal, { ConfirmModal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import {
  createWalletSchema,
  editWalletSchema,
  type CreateWalletFormData,
  type EditWalletFormData,
} from '../../schemas/wallet';

function formatBalance(value: string, currencyCode: string, isCrypto: boolean): string {
  const num = parseFloat(value);
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: isCrypto ? 6 : 2,
  }).format(num) + ' ' + currencyCode;
}

type WalletType = 'cash' | 'bank' | 'crypto' | 'payment_system';
const WALLET_TYPES: WalletType[] = ['cash', 'bank', 'crypto', 'payment_system'];

export default function WalletsPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const queryClient = useQueryClient();
  const { currentEntity } = useEntity();
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [collapsedTypes, setCollapsedTypes] = useState<Set<WalletType>>(new Set());

  const isAdmin = user?.role === 'admin';
  const { success, error: showError } = useToast();
  const [editingWallet, setEditingWallet] = useState<Wallet | null>(null);
  const [deleteWallet, setDeleteWallet] = useState<Wallet | null>(null);
  const [initialBalanceWallet, setInitialBalanceWallet] = useState<Wallet | null>(null);
  const [showOnchainModal, setShowOnchainModal] = useState(false);

  const toggleCollapsed = (type: WalletType) => {
    setCollapsedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const { data, isLoading } = useQuery({
    queryKey: ['wallets', currentEntity?.id],
    queryFn: () => api.admin.wallets.list(currentEntity!.id),
    enabled: !!currentEntity,
  });

  const { data: balancesData } = useQuery({
    queryKey: ['balances', currentEntity?.id],
    queryFn: () => api.reports.balances(currentEntity!.id),
    enabled: !!currentEntity,
  });

  const { data: currencies } = useQuery({
    queryKey: ['currencies'],
    queryFn: () => api.admin.currencies.list(),
  });

  const balancesByWalletId = useMemo(() => {
    const map: Record<string, string> = {};
    if (balancesData?.byType) {
      Object.values(balancesData.byType).forEach((wallets: any[]) => {
        wallets.forEach((w: any) => {
          if (w.walletId) map[w.walletId] = w.balance;
        });
      });
    }
    return map;
  }, [balancesData]);

  const cryptoCodes = useMemo(() => {
    const set = new Set<string>();
    (currencies?.data || []).forEach((c: any) => {
      if (c.type === 'crypto') set.add(c.code);
    });
    return set;
  }, [currencies]);

  const deleteMutation = useMutation({
    mutationFn: (walletId: string) => api.admin.wallets.delete(currentEntity!.id, walletId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      success('Wallet deleted');
      setDeleteWallet(null);
    },
    onError: (err: ApiError) => showError(err.message),
  });

  if (!currentEntity) return null;

  const walletList = data?.data || [];
  const getWalletsByType = (type: WalletType) => walletList.filter(w => w.type === type);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('wallets.title')} - {currentEntity.code}</h1>
        {isAdmin && (
          <div className="flex gap-2">
            <button className="btn btn-secondary" onClick={() => setShowOnchainModal(true)}>
              {t('wallets.verifyOnchain', 'Сверить с блокчейном')}
            </button>
            <button className="btn btn-primary" onClick={() => setShowForm(true)}>
              {t('wallets.newWallet')}
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <p className="text-muted">{tc('actions.loading')}</p>
      ) : (
        <div className="wallet-sections">
          {WALLET_TYPES.map(type => {
            const wallets = getWalletsByType(type);
            const isCollapsed = collapsedTypes.has(type);

            return (
              <div key={type} className="card mb-4">
                <div
                  className="flex items-center gap-3 px-4 py-3 font-semibold border-b border-border cursor-pointer"
                  onClick={() => toggleCollapsed(type)}
                >
                  <span className="text-xs text-foreground-muted">{isCollapsed ? '▶' : '▼'}</span>
                  <span className={`badge badge-${type}`}>{t(`wallets.types.${type}`)}</span>
                  <span className="text-foreground-muted font-normal">({wallets.length})</span>
                </div>

                {!isCollapsed && (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('wallets.fields.name')}</th>
                        <th>{t('wallets.fields.currency')}</th>
                        <th>Address</th>
                        <th>Balance</th>
                        <th>{tc('status.label')}</th>
                        {isAdmin && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {wallets.map((wallet) => {
                        const bal = balancesByWalletId[wallet.id];
                        const isCrypto = cryptoCodes.has(wallet.currencyCode);
                        return (
                          <tr key={wallet.id} className={!wallet.isActive ? 'opacity-60' : ''}>
                            <td><strong>{wallet.name}</strong></td>
                            <td>{wallet.currencyCode}</td>
                            <td className="text-muted">{wallet.address || '-'}</td>
                            <td>
                              {bal !== undefined ? (
                                <span className="font-mono font-semibold">
                                  {formatBalance(bal, wallet.currencyCode, isCrypto)}
                                </span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${wallet.isActive ? 'badge-closed' : 'badge-open'}`}>
                                {wallet.isActive ? tc('status.active') : tc('status.inactive')}
                              </span>
                            </td>
                            {isAdmin && (
                              <td>
                                <div className="flex gap-2">
                                  <Link
                                    to={`/movements?walletId=${wallet.id}&walletName=${encodeURIComponent(wallet.name)}`}
                                    className="btn btn-secondary px-3 py-1 text-[13px]"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    Движения
                                  </Link>
                                  {wallet.type === 'crypto' && wallet.address && (
                                    <Link
                                      to={`/admin/wallets/${wallet.id}/reconciliation`}
                                      className="btn btn-secondary px-3 py-1 text-[13px]"
                                      style={{ textDecoration: 'none' }}
                                    >
                                      Сверить
                                    </Link>
                                  )}
                                  <button
                                    className="btn btn-secondary px-3 py-1 text-[13px]"
                                    onClick={() => setEditingWallet(wallet)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-secondary px-3 py-1 text-[13px]"
                                    onClick={() => setInitialBalanceWallet(wallet)}
                                  >
                                    Initial Balance
                                  </button>
                                  <button
                                    className="btn btn-danger px-3 py-1 text-[13px]"
                                    onClick={() => setDeleteWallet(wallet)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                      {wallets.length === 0 && (
                        <tr>
                          <td colSpan={isAdmin ? 6 : 5} className="text-center text-muted">
                            {t('wallets.noWallets')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(showForm || editingWallet) && (
        <WalletFormModal
          wallet={editingWallet}
          entityId={currentEntity.id}
          currencies={currencies?.data || []}
          onClose={() => { setShowForm(false); setEditingWallet(null); }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['wallets'] });
            setShowForm(false);
            setEditingWallet(null);
            success(editingWallet ? 'Wallet updated' : 'Wallet created');
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteWallet}
        onClose={() => setDeleteWallet(null)}
        onConfirm={() => deleteWallet && deleteMutation.mutate(deleteWallet.id)}
        title="Delete Wallet"
        message={`Are you sure you want to delete "${deleteWallet?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={deleteMutation.isPending}
      />

      {initialBalanceWallet && (
        <InitialBalanceModal
          wallet={initialBalanceWallet}
          entityId={currentEntity.id}
          onClose={() => setInitialBalanceWallet(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['balances'] });
            setInitialBalanceWallet(null);
            success('Initial balance set');
          }}
        />
      )}

      {showOnchainModal && (
        <OnchainVerificationModal
          entityId={currentEntity.id}
          onClose={() => setShowOnchainModal(false)}
        />
      )}


    </div>
  );
}

function WalletFormModal({
  wallet,
  entityId,
  currencies,
  onClose,
  onSuccess,
}: {
  wallet: Wallet | null;
  entityId: string;
  currencies: any[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const toast = useToast();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateWalletFormData | EditWalletFormData>({
     
    resolver: zodResolver((wallet ? editWalletSchema : createWalletSchema) as any),
    defaultValues: wallet
      ? {
          name: wallet.name,
          type: wallet.type,
          currencyCode: wallet.currencyCode,
          address: wallet.address || '',
          description: wallet.description || '',
          isActive: String(wallet.isActive) as unknown as boolean,
        }
      : {
          type: '' as any, // Empty for placeholder
          currencyCode: '', // Empty for placeholder
        },
  });

  // Watch the type field to filter currencies
  const selectedType = watch('type');

  // Filter currencies based on selected wallet type
  const filteredCurrencies = selectedType
    ? currencies.filter(c => {
        if (selectedType === 'crypto') {
          return c.type === 'crypto';
        }
        // For cash, bank, payment_system - show fiat currencies
        return c.type === 'fiat';
      })
    : []; // Empty until type is selected

  // Reset currency when type changes (only for new wallets)
  const handleTypeChange = () => {
    if (!wallet) {
      // Reset currency to empty when type changes - user must select again
      setValue('currencyCode' as any, '');
    }
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateWalletFormData) => api.admin.wallets.create(entityId, {
      name: data.name,
      type: data.type,
      currencyCode: data.currencyCode,
      address: data.address || undefined,
      description: data.description || undefined,
    }),
    onSuccess,
    onError: (err: ApiError) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (data: EditWalletFormData) => api.admin.wallets.update(entityId, wallet!.id, {
      name: data.name,
      type: data.type,
      currencyCode: data.currencyCode,
      address: data.address,
      description: data.description,
      isActive: data.isActive,
    }),
    onSuccess,
    onError: (err: ApiError) => toast.error(err.message),
  });

  const onSubmit = (data: CreateWalletFormData | EditWalletFormData) => {
    if (wallet) {
      updateMutation.mutate(data as EditWalletFormData);
    } else {
      createMutation.mutate(data as CreateWalletFormData);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={wallet ? 'Edit Wallet' : t('wallets.newWallet')}
      size="sm"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-group">
          <label className="form-label">{t('wallets.fields.name')}</label>
          <input
            type="text"
            className={`form-input ${errors.name ? 'border-error' : ''}`}
            placeholder="Main Cash Register"
            {...register('name')}
          />
          {errors.name && (
            <span className="block text-error text-[13px] mt-1">{errors.name.message}</span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('wallets.fields.type')}</label>
          <select
            className={`form-input ${errors.type ? 'border-error' : ''}`}
            {...register('type', { onChange: handleTypeChange })}
          >
            <option value="" disabled>Select type...</option>
            {WALLET_TYPES.map(wt => (
              <option key={wt} value={wt}>{t(`wallets.types.${wt}`)}</option>
            ))}
          </select>
          {errors.type && (
            <span className="block text-error text-[13px] mt-1">{errors.type.message}</span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('wallets.fields.currency')}</label>
          <select
            className={`form-input ${(errors as any).currencyCode ? 'border-error' : ''}`}
            {...register('currencyCode' as any)}
            disabled={!selectedType}
          >
            <option value="" disabled>
              {!selectedType ? 'Select type first...' : 'Select currency...'}
            </option>
            {filteredCurrencies.map((c) => (
              <option key={c.id} value={c.code}>{c.code} - {c.name}</option>
            ))}
          </select>
          {selectedType && filteredCurrencies.length === 0 && (
            <span className="block text-foreground-muted text-xs mt-1 italic">No {selectedType === 'crypto' ? 'crypto' : 'fiat'} currencies available</span>
          )}
          {(errors as any).currencyCode && (
            <span className="block text-error text-[13px] mt-1">{(errors as any).currencyCode.message}</span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('wallets.fields.address')}</label>
          <input
            type="text"
            className="form-input"
            placeholder="Optional"
            {...register('address')}
          />
        </div>

        <div className="form-group">
          <label className="form-label">{t('wallets.fields.description')}</label>
          <textarea
            className="form-input"
            rows={2}
            placeholder="Optional notes"
            {...register('description')}
          />
        </div>

        {wallet && (
          <div className="form-group">
            <label className="form-label">{tc('status.label')}</label>
            <select className="form-input" {...register('isActive' as any)}>
              <option value="true">{tc('status.active')}</option>
              <option value="false">{tc('status.inactive')}</option>
            </select>
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={isPending}>
            {isPending ? tc('actions.loading') : (wallet ? tc('actions.save') : tc('actions.create'))}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function InitialBalanceModal({
  wallet,
  entityId,
  onClose,
  onSuccess,
}: {
  wallet: Wallet;
  entityId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const mutation = useMutation({
    mutationFn: () =>
      api.admin.wallets.setInitialBalance(entityId, wallet.id, {
        amount,
        date,
        currency: wallet.currencyCode,
      }),
    onSuccess,
    onError: (err: ApiError) => toast.error(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || !date) return;
    mutation.mutate();
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={`Initial Balance — ${wallet.name}`} size="sm">
      <form onSubmit={handleSubmit}>
        <p className="text-sm text-foreground-muted mb-4">
          Set the starting balance for <strong>{wallet.name}</strong> ({wallet.currencyCode}).
          This creates a manual ledger entry. Any previous initial balance will be replaced.
        </p>

        <div className="form-group">
          <label className="form-label">Amount ({wallet.currencyCode})</label>
          <input
            type="number"
            step="any"
            className="form-input"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            autoFocus
          />
        </div>

        <div className="form-group">
          <label className="form-label">Date</label>
          <input
            type="date"
            className="form-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={mutation.isPending}>
            {mutation.isPending ? tc('actions.loading') : 'Set Balance'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function OnchainVerificationModal({
  entityId,
  onClose,
}: {
  entityId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation('admin');

  const { data, isLoading, error } = useQuery({
    queryKey: ['wallets', 'verify-onchain', entityId],
    queryFn: () => api.admin.wallets.verifyOnchain(entityId),
    staleTime: 0,
    gcTime: 0,
  });

  const results = data?.data || [];
  const hasDiscrepancies = results.some(
    (r) => r.diff !== null && Math.abs(parseFloat(r.diff)) > 0.01
  );

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={t('wallets.verifyOnchain', 'Сверка с блокчейном')}
      size="lg"
    >
      {isLoading ? (
        <div className="text-center py-8">
          <div className="spinner mb-3" />
          <p className="text-muted">{t('wallets.onchain.loading', 'Запрос балансов из блокчейна...')}</p>
        </div>
      ) : error ? (
        <div className="text-center py-8 text-error">
          {(error as ApiError).message || 'Failed to verify'}
        </div>
      ) : (
        <div>
          {hasDiscrepancies && (
            <div className="mb-4 p-3 rounded-lg" style={{ background: 'var(--ctp-red)', color: 'var(--ctp-base)', opacity: 0.9 }}>
              {t('wallets.onchain.discrepanciesFound', 'Обнаружены расхождения!')}
            </div>
          )}

          <table className="table">
            <thead>
              <tr>
                <th>{t('wallets.onchain.wallet', 'Кошелёк')}</th>
                <th>{t('wallets.onchain.network', 'Сеть')}</th>
                <th>{t('wallets.onchain.systemBalance', 'Баланс (система)')}</th>
                <th>{t('wallets.onchain.onchainBalance', 'Баланс (блокчейн)')}</th>
                <th>{t('wallets.onchain.diff', 'Разница')}</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => {
                const diffNum = r.diff !== null ? parseFloat(r.diff) : null;
                const hasDiff = diffNum !== null && Math.abs(diffNum) > 0.01;
                return (
                  <tr key={r.walletId}>
                    <td>
                      <strong>{r.name}</strong>
                      <div className="text-xs text-muted font-mono" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.address}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${r.network === 'tron' ? 'badge-closed' : r.network === 'ethereum' ? 'badge-open' : 'badge-secondary'}`}>
                        {r.network.toUpperCase()}
                      </span>
                    </td>
                    <td className="font-mono">
                      {parseFloat(r.systemBalance).toFixed(2)} {r.currencyCode}
                    </td>
                    <td className="font-mono">
                      {r.error ? (
                        <span className="text-error text-xs">{r.error}</span>
                      ) : (
                        <>{parseFloat(r.onchainBalance || '0').toFixed(2)} {r.currencyCode}</>
                      )}
                    </td>
                    <td>
                      {r.error ? (
                        <span className="text-muted">—</span>
                      ) : (
                        <span
                          className={`font-mono font-semibold ${hasDiff ? 'text-error' : 'text-success'}`}
                        >
                          {diffNum !== null ? (diffNum > 0 ? '+' : '') + diffNum.toFixed(2) : '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {results.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    {t('wallets.onchain.noWallets', 'Нет крипто-кошельков с адресами')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="flex justify-end mt-4">
            <button className="btn btn-secondary" onClick={onClose}>
              Закрыть
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

